import { Droppable, Draggable, DroppableProvided, DraggableProvided, DraggableStateSnapshot } from "react-beautiful-dnd";
import { MdAdd, MdSettings } from "react-icons/md";
import { LayoutColumn as LayoutColumnType, LayoutSectionConfig } from "./types";
import { formatWidthLabel } from "./layoutUtils";
import { LayoutSectionChip } from "./LayoutSectionChip";

interface LayoutColumnProps {
  column: LayoutColumnType;
  rowId: string;
  onAddSection: () => void;
  onEditSection: (sectionIndex: number) => void;
  onDeleteSection: (sectionIndex: number) => void;
  onOpenSettings: () => void;
  onDeleteColumn: () => void;
  canDelete: boolean;
}

export const LayoutColumnComponent = ({
  column,
  rowId,
  onAddSection,
  onEditSection,
  onDeleteSection,
  onOpenSettings,
  onDeleteColumn,
  canDelete,
}: LayoutColumnProps) => {
  const droppableId = `${rowId}:${column.id}`;
  const isHiddenOnMobile = !column.mobileConfig.visible;

  return (
    <div
      className={`relative rounded-lg border-2 border-dashed p-2 ${
        isHiddenOnMobile
          ? "border-amber-300 bg-amber-50/50 dark:border-amber-600 dark:bg-amber-900/10"
          : "border-slate-300 bg-slate-50/50 dark:border-slate-600 dark:bg-slate-800/30"
      }`}
    >
      {/* Column header */}
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <span className="text-xs font-medium text-slate-500 dark:text-slate-400">
            {formatWidthLabel(column.width)}
          </span>
          {isHiddenOnMobile && (
            <span className="rounded bg-amber-200 px-1.5 py-0.5 text-[10px] font-medium text-amber-800 dark:bg-amber-800/40 dark:text-amber-300">
              ukryta na mobile
            </span>
          )}
        </div>
        <div className="flex items-center gap-0.5">
          <button
            type="button"
            onClick={onOpenSettings}
            className="rounded p-1 text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700"
            title="Ustawienia kolumny"
          >
            <MdSettings size={14} />
          </button>
          {canDelete && (
            <button
              type="button"
              onClick={onDeleteColumn}
              className="rounded p-1 text-xs text-slate-400 hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/30 dark:hover:text-red-400"
              title="Usuń kolumnę"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Droppable section area */}
      <Droppable droppableId={droppableId} type="SECTION">
        {(provided: DroppableProvided) => (
          <div
            {...provided.droppableProps}
            ref={provided.innerRef}
            className="min-h-[40px] space-y-1"
          >
            {column.sections.map((section, index) => (
              <Draggable
                key={`${droppableId}-${index}`}
                draggableId={`${droppableId}-${index}`}
                index={index}
              >
                {(provided: DraggableProvided, snapshot: DraggableStateSnapshot) => (
                  <div ref={provided.innerRef} {...provided.draggableProps}>
                    <LayoutSectionChip
                      section={section}
                      onEdit={() => onEditSection(index)}
                      onDelete={() => onDeleteSection(index)}
                      dragHandleProps={provided.dragHandleProps}
                      isDragging={snapshot.isDragging}
                    />
                  </div>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>

      {/* Add section button */}
      <button
        type="button"
        onClick={onAddSection}
        className="mt-2 flex w-full items-center justify-center gap-1 rounded-md border border-dashed border-slate-300 py-1.5 text-xs text-slate-500 transition-colors hover:border-indigo-400 hover:bg-indigo-50 hover:text-indigo-600 dark:border-slate-600 dark:text-slate-400 dark:hover:border-indigo-500 dark:hover:bg-indigo-900/20 dark:hover:text-indigo-400"
      >
        <MdAdd size={14} />
        Dodaj sekcję
      </button>
    </div>
  );
};
